// src/features/pdf/components/DraggableFileList.tsx
import React from 'react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import DraggableFileItem from './DraggableFileItem';
import { Box } from '@mui/material';

// Define FileWithId interface
interface FileWithId {
    id: string;
    file: File;
}

interface DraggableFileListProps {
    files: FileWithId[];
    onReorder: (newFiles: FileWithId[]) => void;
    onDelete: (fileId: string) => void;
    onFileClick?: (file: File) => void;
    isDraggable?: boolean;
}

const DraggableFileList: React.FC<DraggableFileListProps> = ({
                                                                 files,
                                                                 onReorder,
                                                                 onDelete,
                                                                 onFileClick = () => {},
                                                                 isDraggable = true,
                                                             }) => {
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Handle drag end to reorder files using unique IDs
    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = files.findIndex(f => f.id === active.id);
            const newIndex = files.findIndex(f => f.id === over.id);
            onReorder(arrayMove(files, oldIndex, newIndex));
        }
    };

    // Use unique IDs for SortableContext
    const fileIds = files.map(f => f.id);

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
        >
            <SortableContext items={fileIds} strategy={verticalListSortingStrategy}>
                <Box sx={{ width: '100%', mt: 2 }}>
                    {files.map(fileWithId => (
                        <DraggableFileItem
                            key={fileWithId.id}
                            id={fileWithId.id}
                            file={fileWithId.file}
                            onDelete={onDelete}
                            onClick={onFileClick}
                            isDraggable={isDraggable}
                        />
                    ))}
                </Box>
            </SortableContext>
        </DndContext>
    );
};

export default DraggableFileList;